<template>
  <div
    :class="classes(n(), n(`--${shape}`))"
    :style="avatarStyle"
    role="img"
    :aria-label="alt"
  >
    <!-- 默认头像：如果没有 src、图片加载失败，或（图片加载中且 showDefaultOnLoad 为 true）时显示默认头像 -->
    <div
      v-if="!src || imageError || (src && !imageLoaded && showDefaultOnLoad)"
      :class="n('default-icon')"
    >
      <img
        :src="defaultAvatarSrc"
        :alt="alt"
        :class="n('default-icon-image')"
      >
    </div>
    <!-- 图片头像：如果提供了 src 且未出错，则尝试加载图片 -->
    <img
      v-if="src && !imageError"
      ref="imageElement"
      :src="src"
      :alt="alt"
      :class="n('image')"
      :style="{ opacity: imageLoaded ? 1 : 0 }"
      @load="handleImageLoad"
      @error="handleImageError"
    >
  </div>
</template>

<script lang="ts">
import {
  computed,
  defineComponent,
  nextTick,
  onMounted,
  ref,
  watch,
} from 'virtual-vue';
import { props } from './props';
import { emits } from './emits';
import { createNamespace } from '../utils/components';
import defaultAvatarSrc from './default-avatar.svg';

const { name, n, classes } = createNamespace('avatar');

export default defineComponent({
  name,
  props,
  emits,
  setup(props) {
    // 图片加载状态管理
    const imageLoaded = ref(false);
    const imageError = ref(false);
    const imageElement = ref<HTMLImageElement | null>(null);

    // 检查图片是否已经加载完成（处理缓存情况）
    const checkImageLoaded = () => {
      if (imageElement.value && imageElement.value.complete) {
        if (imageElement.value.naturalWidth > 0) {
          imageLoaded.value = true;
          imageError.value = false;
        } else {
          imageLoaded.value = false;
          imageError.value = true;
        }
      }
    };

    // 当 src 变化时，重置加载状态
    watch(
      () => props.src,
      (newSrc) => {
        if (newSrc) {
          imageLoaded.value = false;
          imageError.value = false;
          // 等待 DOM 更新后检查图片是否已加载
          nextTick(() => {
            checkImageLoaded();
          });
        } else {
          imageLoaded.value = false;
          imageError.value = true;
        }
      },
      { immediate: true },
    );

    // 图片加载成功处理
    const handleImageLoad = () => {
      imageLoaded.value = true;
      imageError.value = false;
    };

    // 图片加载失败处理
    const handleImageError = () => {
      imageLoaded.value = false;
      imageError.value = true;
    };

    // 动态设置尺寸（使用 CSS 变量）
    const avatarStyle = computed(() => ({
      '--avatar-size': props.size,
    }));

    onMounted(() => {
      // 组件挂载后检查图片是否已加载（处理缓存情况）
      checkImageLoaded();
    });

    return {
      n,
      classes,
      avatarStyle,
      imageLoaded,
      imageError,
      imageElement,
      handleImageLoad,
      handleImageError,
      defaultAvatarSrc,
      shape: computed(() => props.shape),
      src: computed(() => props.src),
      alt: computed(() => props.alt),
      showDefaultOnLoad: computed(() => props.showDefaultOnLoad),
    };
  },
});
</script>
